import { EMPTY, Observable, Subscription, concatMap, finalize, from, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';


/** Framework-independent scheduler for authoritative API-credit balance reads. */
export class ApiTokenBalanceReconciler {
	// Incremental delays produce reads at approximately 0, 5, 15, 30, 60, 90,
	// and 120 seconds. Each schedule is serialized; the global sequence guard below
	// prevents a slower request from another schedule overwriting a newer snapshot.
	private readonly SETTLEMENT_REFRESH_DELAYS_MS: number[] = [0, 5000, 10000, 15000, 30000, 30000, 30000];
	private immediateRefreshSubscription: Subscription | null = null;
	private readonly settlementSubscriptions: Set<Subscription> = new Set<Subscription>();
	private nextRequestSequence: number = 0;
	private latestAppliedRequestSequence: number = 0;

	constructor(
		private getBalance: () => Observable<number>
	) {}

	refresh(setBalance: TypeApiTokenBalanceSetter): void {
		this.immediateRefreshSubscription?.unsubscribe();
		const subscriptionRef: { value: Subscription | null } = { value: null };
		const refreshSubscription: Subscription = this.createRefreshSequence([0], setBalance, () => {
			if (this.immediateRefreshSubscription === subscriptionRef.value) this.immediateRefreshSubscription = null;
		});
		subscriptionRef.value = refreshSubscription;
		this.immediateRefreshSubscription = refreshSubscription;
	}

	reconcileAfterSettlement(setBalance: TypeApiTokenBalanceSetter): void {
		const subscriptionRef: { value: Subscription | null } = { value: null };
		const settlementSubscription: Subscription = this.createRefreshSequence(this.SETTLEMENT_REFRESH_DELAYS_MS, setBalance, () => {
			if (subscriptionRef.value) this.settlementSubscriptions.delete(subscriptionRef.value);
		});
		subscriptionRef.value = settlementSubscription;
		this.settlementSubscriptions.add(settlementSubscription);
	}

	reconcileAfterSseError(error: unknown, setBalance: TypeApiTokenBalanceSetter): void {
		// SecureSse preserves parsed event:error payloads as objects. Fetch, reader,
		// parsing, and premature-EOF failures are Error instances and may settle later.
		if (isSseTransportError(error)) this.reconcileAfterSettlement(setBalance);
		else this.refresh(setBalance);
	}

	private createRefreshSequence(delaysMs: number[], setBalance: TypeApiTokenBalanceSetter, onFinalize: () => void): Subscription {
		return from(delaysMs).pipe(
			concatMap((delayMs: number) => timer(delayMs).pipe(
				switchMap(() => {
					const requestSequence: number = ++this.nextRequestSequence;
					return this.getBalance().pipe(map((balance: number): ISequencedApiTokenBalance => ({
						requestSequence,
						balance
					})));
				}),
				catchError(() => EMPTY)
			)),
			finalize(onFinalize)
		).subscribe((result: ISequencedApiTokenBalance) => {
			if (result.requestSequence < this.latestAppliedRequestSequence) return;
			this.latestAppliedRequestSequence = result.requestSequence;
			setBalance(result.balance);
		});
	}
}

export type TypeApiTokenBalanceSetter = (balance: number) => void;

export function isSseTransportError(error: unknown): boolean {
	return error instanceof Error;
}

interface ISequencedApiTokenBalance {
	requestSequence: number;
	balance		   : number;
}
